import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Modal, Text, Input } from 'v3/components';
import Space from 'v3/components/Space';
import { StyledModalButton } from 'v3/pages/Policy/styles';
import { cooperplaceApi } from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import { validateOpName } from '../../utils';
import { usePolicyRegister } from '../../context';

export function OperationsModal() {
  const {
    operationModal,
    setOperations,
    operations,
    setErrors,
    currentOpIndex,
    setCurrentOpIndex,
    editAction,
    setEditAction,
    policyClients,
  } = usePolicyRegister();

  const [newOperation, setNewOperation] = useState('');
  const [opError, setOpError] = useState({});
  const [validatingOperation, setValidatingOperation] = useState(false);
  const snackbar = useSnackbar();

  const cleanText = (val) =>
    DOMPurify.sanitize(val ?? '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

  const normalizeOperationName = (val) => cleanText(val).replace(/\s+/g, ' ').trim();
  const setError = (error) => setOpError(error || {});

  async function handleAdd() {
    try {
      setValidatingOperation(true);
      const operationName = normalizeOperationName(newOperation);
      const oldOpNames = operations?.map(op => normalizeOperationName(op?.name).toLowerCase());
      const isValid = validateOpName(oldOpNames, operationName);
      if (!isValid) {
        setError({
          name: 'Nome de operação já está em uso!',
        });
        setValidatingOperation(false);
        return;
      }

      await cooperplaceApi.post(
        'policy-validator/operation/validate',
        {
          action: 'update',
          operationsNames: [operationName],
          customers: policyClients?.map(client => client.id),
          type:'operation'
        }
      );
      setEditAction(null)
      setOperations(old => [...old, { name: operationName, products: [] }]);
      setNewOperation('');
      setCurrentOpIndex(null);
      setOpError({});
      setErrors({});
      setValidatingOperation(false);
      operationModal.close();
    } catch (error) {
      const message = error?.response?.data?.error || 'Erro ao validar operação!';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
      setValidatingOperation(false);
    }
  }

  async function handleEdit() {
    try {
      setValidatingOperation(true);
      const operationName = normalizeOperationName(newOperation);

      if (operationName === operations[currentOpIndex]?.name?.trim()) {
        setError({ name: 'Informe um nome diferente!' });
        setValidatingOperation(false);
        return;
      }

      const oldOpNames = operations
        ?.filter((_, idx) => idx !== currentOpIndex)
        ?.map(op => normalizeOperationName(op?.name).toLowerCase());
      const isValid = validateOpName(oldOpNames, operationName);

      if (!isValid) {
        setError({
          name: 'Nome de operação já está em uso!',
        });
        setValidatingOperation(false);
        return;
      }

      await cooperplaceApi.post(
        'policy-validator/operation/validate',
        {
          action: 'update',
          operationsNames: [operationName],
          customers: policyClients?.map(client => client.id),
          type: 'operation',
        }
      );

      let newState = [...operations];
      newState[currentOpIndex].name = operationName;
      setOperations(newState);

      setNewOperation('');
      setError({});
      setEditAction(null)
      setCurrentOpIndex(null);
      setValidatingOperation(false);
      operationModal.close();
    } catch (error) {
      const message = error?.response?.data?.error || 'Erro ao validar operação!';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
      setValidatingOperation(false);
    }
  }

  function handleCloseModal() {
    if (validatingOperation) return;
    setNewOperation('');
    setOpError({});
    setCurrentOpIndex(null);
    operationModal.close();
  }

  useEffect(() => {
    if (currentOpIndex !== null && operationModal.isOpen) {
      setNewOperation(operations[currentOpIndex].name);
    }
  }, [currentOpIndex, operationModal]);

  function handleDuplicate() {
    const operationName = normalizeOperationName(newOperation);

    if (operationName === operations[currentOpIndex]?.name?.trim()) {
      setError({ name: 'Informe um nome diferente!' });
      return;
    }
    const oldOpNames = operations?.map(op => normalizeOperationName(op?.name).toLowerCase());
    const isValid = validateOpName(oldOpNames, operationName);

    if (!isValid) {
      setError({
        name: 'Nome de operação já está em uso!',
      });
      return;
    }

    const operation = operations[currentOpIndex]
    const duplicateOperation = JSON.parse(JSON.stringify(operation))
    delete duplicateOperation.id;
    duplicateOperation.name = operationName;
    for (const product of duplicateOperation.products) {
      delete product.id
      for (const vehicle of product.vehicles) {
        delete vehicle.id
        for (const range of vehicle.ranges) {
          delete range.id
          for (const equipment of range.monitoringEquipaments) {
            delete equipment.id;
          }
        }
      }
    }

    setOperations([...operations, duplicateOperation])
    setNewOperation('');
    setError({});
    setEditAction(null)
    setCurrentOpIndex(null);
    operationModal.close();
  }

  function onClick() {
    if (editAction === 'duplicate') {
      handleDuplicate()
    } else {
      currentOpIndex !== null ? handleEdit() : handleAdd()
    }
  }

  const label = useMemo(() => {
    if (editAction == 'duplicate') {
      return 'Duplicar operação'
    }

    if (currentOpIndex !== null) {
      return 'Editar operação'
    }

    return 'Nova operação'
  }, [editAction, currentOpIndex])

  return (
    <Modal
      size="lg"
      show={operationModal.isOpen}
      handleClose={handleCloseModal}
      heading={
        <Text type="body" color="dark" weight="400">
          {label}
        </Text>
      }
      body={
        <div>
          <Input
            label="Nome da operação *"
            value={newOperation}
            onChange={(e) => {
              const val = e?.target?.value ?? '';
              const safeText = cleanText(val);
              if (safeText.length > 255) return;
              setNewOperation(safeText);
            }}
            error={opError?.name}
          />
        </div>
      }
      footer={
        <Space>
          <StyledModalButton
            size="sm"
            onClick={onClick}
            disabled={normalizeOperationName(newOperation)?.length === 0}
            loading={validatingOperation}
          >
            <Text>{currentOpIndex !== null ? 'Salvar' : 'Adicionar'}</Text>
          </StyledModalButton>
        </Space>
      }
    />
  );
}
